/**
 * Slice 1a — Bexio invoice-draft PREVIEW. READ-ONLY and BEXIO-WRITE-FREE.
 *
 * Shows which rent-invoice drafts WOULD theoretically be needed for the selected
 * month and surfaces blockers (missing contact mapping / missing tenancy reference
 * / missing tax-account config) instead of creating anything. It NEVER creates a
 * Bexio draft, NEVER writes, and makes NO Bexio API call — there is no
 * Create/Issue/Send/Mark-as-sent/Cancel/Delete/Auto-contact action anywhere here.
 *
 * one_time charges are shown SEPARATELY from recurring rent (never summed in).
 * ``already_exists_local`` is deliberately "not available until Outbox" — it is NOT
 * derived from Bexio mirror rows (no authoritative rent-month attribution).
 *
 * Self-contained presentational primitives (mirrors the sibling Bexio sections).
 */
import React from "react";

function fmtMoney(v, currency) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${currency || "CHF"} ${n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function Badge({ children, tone = "neutral" }) {
  const tones = {
    neutral: "border-input text-muted-foreground",
    good: "border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400",
    warn: "border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300",
    bad: "border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-[10px] border border-border bg-card p-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-[18px] font-bold text-foreground">{value}</div>
    </div>
  );
}

const _MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

function MonthSelector({ year, month, onMonthChange, disabled }) {
  const now = new Date();
  const thisYear = now.getFullYear();
  const years = [thisYear - 2, thisYear - 1, thisYear, thisYear + 1];
  return (
    <div className="flex items-center gap-2 text-[12px] text-foreground">
      <label className="text-muted-foreground">Vorschau-Monat:</label>
      <select
        aria-label="Vorschau-Monat"
        className="rounded-[8px] border border-input bg-card px-2 py-1 text-[12px] text-foreground"
        value={month}
        disabled={disabled}
        onChange={(e) => onMonthChange(year, Number(e.target.value))}
      >
        {_MONTHS_DE.map((m, i) => (
          <option key={i + 1} value={i + 1}>
            {m}
          </option>
        ))}
      </select>
      <select
        aria-label="Vorschau-Jahr"
        className="rounded-[8px] border border-input bg-card px-2 py-1 text-[12px] text-foreground"
        value={year}
        disabled={disabled}
        onChange={(e) => onMonthChange(Number(e.target.value), month)}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}

function ContactBadge({ status }) {
  const map = {
    mapped: ["good", "gemappt"],
    missing: ["bad", "kein Bexio-Kontakt"],
    ambiguous: ["warn", "mehrdeutig"],
    not_supported_yet: ["neutral", "noch nicht unterstützt"],
  };
  const [tone, label] = map[status] || ["neutral", status || "—"];
  return <Badge tone={tone}>{label}</Badge>;
}

const _STATUS_LABEL = {
  would_be_blocked_missing_reference: ["bad", "blockiert: Referenz fehlt"],
  would_be_blocked_missing_contact: ["bad", "blockiert: Kontakt fehlt"],
  would_be_blocked_missing_tax_account_config: [
    "warn",
    "blockiert: Steuer/Konto-Konfig fehlt",
  ],
  would_be_ready_after_blockers_resolved: ["good", "bereit nach Blocker-Klärung"],
  preview_only: ["neutral", "Vorschau"],
};

function StatusBadge({ status }) {
  const [tone, label] = _STATUS_LABEL[status] || ["neutral", status || "—"];
  return <Badge tone={tone}>{label}</Badge>;
}

function Table({ rows, currency }) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) {
    return (
      <div className="rounded-[10px] border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
        Keine zahlungspflichtigen Mietverhältnisse für den gewählten Monat.
      </div>
    );
  }
  const head = [
    "Mieter / Referenz",
    "Einheit / Zimmer",
    "Erwartete Miete (anteilig)",
    "Einmalig (separat)",
    "Vorschau-Total",
    "Bexio-Kontakt",
    "Blocker",
    "Status",
  ];
  return (
    <div className="overflow-x-auto rounded-[10px] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-card">
            {head.map((h, i) => (
              <th
                key={i}
                className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            const oneTimeItems = Array.isArray(r.one_time_items)
              ? r.one_time_items
              : [];
            const refMissing = (r.reference_blockers || []).includes(
              "missing_tenancy_reference"
            );
            return (
              <tr
                key={r.tenancy_id}
                className="border-t border-border align-top text-foreground"
              >
                <td className="px-3 py-2">
                  <div className="font-semibold text-foreground">
                    {r.tenant_name || "—"}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.tenancy_reference_number || (
                      <span className="text-red-700 dark:text-red-300">Referenz fehlt</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div>{r.unit_label || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.room_label || "—"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {fmtMoney(r.recurring_rent_amount_chf, currency)}
                </td>
                <td className="px-3 py-2">
                  {oneTimeItems.length === 0 ? (
                    "—"
                  ) : (
                    <div className="space-y-0.5">
                      {oneTimeItems.map((it, i) => (
                        <div key={i} className="whitespace-nowrap">
                          <span className="text-[10px] text-muted-foreground">
                            {it.type || it.description || "einmalig"}:{" "}
                          </span>
                          {fmtMoney(it.amount_chf, currency)}
                        </div>
                      ))}
                      <div className="whitespace-nowrap border-t border-border pt-0.5 text-[10px] text-muted-foreground">
                        Summe {fmtMoney(r.one_time_total_chf, currency)}
                      </div>
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-semibold text-foreground">
                  {fmtMoney(r.total_preview_amount_chf, currency)}
                </td>
                <td className="px-3 py-2">
                  <ContactBadge status={r.contact_mapping_status} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {refMissing ? (
                      <Badge tone="bad">Referenz fehlt</Badge>
                    ) : null}
                    {(r.config_blockers || []).length > 0 ? (
                      <Badge tone="warn">Steuer/Konto fehlt</Badge>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function BexioInvoiceDraftPreviewSection({
  data,
  loading,
  error,
  onRetry,
  year,
  month,
  onMonthChange,
}) {
  const malformed = data && (!data.summary || !data.rows || !data.mode);
  const isError = !!error || (!loading && (!data || malformed));
  const currency = data?.summary?.currency || "CHF";
  const s = data?.summary;

  return (
    <section className="mb-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-[14px] font-semibold text-foreground">
            Bexio-Rechnungsentwurf — Vorschau (kein Schreibzugriff)
          </h2>
          <p className="max-w-[760px] text-[11px] text-muted-foreground">
            Zeigt, welche Miet-Rechnungsentwürfe für den gewählten Monat theoretisch
            nötig wären, und macht Blocker sichtbar. Einmalige Gebühren werden
            getrennt von der wiederkehrenden Miete ausgewiesen.
          </p>
        </div>
        <MonthSelector
          year={year}
          month={month}
          onMonthChange={onMonthChange}
          disabled={loading}
        />
      </div>

      {/* Fixed "preview only — nothing is created" banner. */}
      <div className="mb-3 rounded-[10px] border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 p-3 text-[12px] text-blue-700 dark:text-blue-300">
        Nur Vorschau — <strong>es wird kein Bexio-Entwurf erstellt</strong>, kein
        Bexio-Call ausgeführt und nichts gespeichert. „Bereits vorhanden" wird bewusst
        nicht aus Bexio-Mirror-Daten abgeleitet (
        <code>not_available_until_outbox</code>). tax_id/account_id/MWST sind ein
        offener Blocker; api_reference ist ein nicht finaler Kandidat.
      </div>

      {loading ? (
        <div className="flex items-center gap-2 rounded-[10px] border border-border bg-card p-6 text-[12px] text-muted-foreground">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-input border-t-primary" />
          Lädt Rechnungsentwurf-Vorschau …
        </div>
      ) : isError ? (
        <div className="rounded-[10px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-[13px] text-red-700 dark:text-red-300">
          <div>{error || "Vorschau konnte nicht geladen werden."}</div>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20"
            >
              Erneut versuchen
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Zeilen" value={s ? String(s.row_count) : "—"} />
            <StatCard
              label="Miete (Summe, anteilig)"
              value={s ? fmtMoney(s.recurring_total_chf, currency) : "—"}
            />
            <StatCard
              label="Einmalig (Summe, separat)"
              value={s ? fmtMoney(s.one_time_total_chf, currency) : "—"}
            />
            <StatCard
              label="Blockiert (Konfig/Kontakt/Referenz)"
              value={
                s
                  ? String(
                      s.blocked_missing_tax_account_config_count +
                        s.blocked_missing_contact_count +
                        s.blocked_missing_reference_count
                    )
                  : "—"
              }
            />
          </div>
          <Table rows={data?.rows} currency={currency} />
        </>
      )}
    </section>
  );
}
